import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import { comics, categories } from '@/mocks/comics';

const PAGE_SIZE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const isAll = slug === 'all' || !slug;

  useEffect(() => {
    setCurrentPage(1);
  }, [slug, sortBy]);

  const filteredComics = useMemo(() => {
    let result = isAll ? [...comics] : comics.filter((c) => c.categorySlug === slug);
    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return result;
  }, [slug, sortBy, isAll]);

  const totalPages = Math.ceil(filteredComics.length / PAGE_SIZE);
  const pagedComics = filteredComics.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: isAll ? '全部漫画' : currentCategory?.name || '' },
  ];

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen">
        <div className="pt-24 pb-6 max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {isAll ? '全部漫画' : currentCategory?.name || ''}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {isAll
                ? `共收录 ${comics.length} 部韩漫作品，持续更新中`
                : currentCategory?.description || ''}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  (isAll && cat.slug === 'all') || cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{filteredComics.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-lg p-0.5">
              {[
                { key: 'latest' as const, label: '最新' },
                { key: 'popular' as const, label: '最热' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {pagedComics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画作品</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
              {pagedComics.map((comic) => (
                <ComicCard key={comic.slug} comic={comic} />
              ))}
            </div>
          )}

          <div className="mt-10 mb-6">
            <Pagination current={currentPage} total={totalPages} onPageChange={setCurrentPage} />
          </div>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: isAll ? '全部漫画 - 韩漫在线' : `${currentCategory?.name} - 韩漫在线`,
            description: isAll ? '韩漫在线全部漫画作品列表，免费在线阅读。' : currentCategory?.description || '',
            url: isAll ? 'https://hanmanonline.com/category/all' : `https://hanmanonline.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫在线',
              url: 'https://hanmanonline.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanmanonline.com' },
                { '@type': 'ListItem', position: 2, name: isAll ? '全部漫画' : currentCategory?.name || '' },
              ],
            },
          }),
        }}
      />
    </>
  );
}